(function () {
  const HOST_SELECTOR = '[id^="alia-root-"]';
  const BUTTON_SELECTOR = 'button[id^="alia-"]';
  const POPUP_ROOT_SELECTOR = '[id^="alia-popup-root-"]';

  const BUTTON_RADIUS = '4px';
  const LAUNCHER_OFFSET = '20px -8px';
  const BOOTSTRAP_TIMEOUT_MS = 20000;
  const BOOTSTRAP_INTERVAL_MS = 250;
  const MAX_LAUNCHER_WIDTH = 420;
  const MAX_LAUNCHER_HEIGHT = 120;

  const BUTTON_STYLE_PROPS = ['border-radius', 'overflow'];
  const OFFSET_STYLE_PROPS = ['translate'];

  let documentObserver = null;
  let shadowObserver = null;
  let hostAttributeObserver = null;
  let rafScheduled = false;
  let bootstrapTimer = null;
  let bootstrapDeadline = 0;
  let activeHost = null;
  let activeButton = null;
  let activeOffsetTarget = null;

  const originalInlineStyles = new WeakMap();

  function getHost() {
    return document.querySelector(HOST_SELECTOR);
  }

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function rememberStyle(element, property) {
    if (!element) return;

    let snapshot = originalInlineStyles.get(element);
    if (!snapshot) {
      snapshot = new Map();
      originalInlineStyles.set(element, snapshot);
    }

    if (!snapshot.has(property)) {
      snapshot.set(property, {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property)
      });
    }
  }

  function setStyle(element, property, value, priority) {
    if (!element) return;

    rememberStyle(element, property);

    if (
      element.style.getPropertyValue(property) === value &&
      element.style.getPropertyPriority(property) === (priority || '')
    ) {
      return;
    }

    element.style.setProperty(property, value, priority || '');
  }

  function restoreStyles(element, properties) {
    if (!element) return;

    const snapshot = originalInlineStyles.get(element);
    if (!snapshot) return;

    properties.forEach((property) => {
      const original = snapshot.get(property);
      if (!original) {
        element.style.removeProperty(property);
        return;
      }

      if (original.value) {
        element.style.setProperty(property, original.value, original.priority);
      } else {
        element.style.removeProperty(property);
      }

      snapshot.delete(property);
    });

    if (!snapshot.size) {
      originalInlineStyles.delete(element);
    }
  }

  function clearActiveStyles() {
    if (activeOffsetTarget) {
      restoreStyles(activeOffsetTarget, OFFSET_STYLE_PROPS);
      activeOffsetTarget = null;
    }

    if (activeButton) {
      restoreStyles(activeButton, BUTTON_STYLE_PROPS);
      activeButton = null;
    }
  }

  function stopBootstrapLoop() {
    if (!bootstrapTimer) return;
    window.clearTimeout(bootstrapTimer);
    bootstrapTimer = null;
    bootstrapDeadline = 0;
  }

  function disconnectHostObservers() {
    if (shadowObserver) {
      shadowObserver.disconnect();
      shadowObserver = null;
    }

    if (hostAttributeObserver) {
      hostAttributeObserver.disconnect();
      hostAttributeObserver = null;
    }
  }

  function isDialogOpen(host) {
    if (!host) return false;

    return (
      host.getAttribute('role') === 'dialog' ||
      host.getAttribute('aria-modal') === 'true'
    );
  }

  function getRect(element) {
    if (!element || typeof element.getBoundingClientRect !== 'function') {
      return null;
    }

    const rect = element.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) {
      return null;
    }

    return rect;
  }

  function isLauncherSized(rect) {
    if (!rect) return false;

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.width <= MAX_LAUNCHER_WIDTH &&
      rect.height <= MAX_LAUNCHER_HEIGHT
    );
  }

  function getButtonLabel(button) {
    return normalizeText(
      [
        button && button.textContent,
        button && button.getAttribute && button.getAttribute('aria-label'),
        button && button.getAttribute && button.getAttribute('title')
      ]
        .filter(Boolean)
        .join(' ')
    );
  }

  function isLauncherLabel(label) {
    if (!label) return false;

    return (
      label.includes('mystery discount') ||
      (label.includes('mystery') && label.includes('discount'))
    );
  }

  function findPopupRoot(shadowRoot) {
    return shadowRoot ? shadowRoot.querySelector(POPUP_ROOT_SELECTOR) : null;
  }

  function findPopupChild(button, popupRoot) {
    if (!button || !popupRoot) return null;

    let node = button;
    while (node && node.parentElement && node.parentElement !== popupRoot) {
      node = node.parentElement;
    }

    if (node && node.parentElement === popupRoot) {
      return node;
    }

    return null;
  }

  function chooseOffsetTarget(wrapper, button) {
    const buttonRect = getRect(button);
    const wrapperRect = getRect(wrapper);

    if (!wrapper || !wrapperRect || !isLauncherSized(wrapperRect)) {
      return button;
    }

    if (!buttonRect) {
      return wrapper;
    }

    const widthDelta = Math.abs(wrapperRect.width - buttonRect.width);
    const heightDelta = Math.abs(wrapperRect.height - buttonRect.height);

    if (widthDelta > 48 || heightDelta > 48) {
      return button;
    }

    return wrapper;
  }

  function scoreButton(button, popupRoot) {
    const label = getButtonLabel(button);
    if (!isLauncherLabel(label)) {
      return null;
    }

    const wrapper =
      findPopupChild(button, popupRoot) || button.parentElement || button;
    const buttonRect = getRect(button);
    const wrapperRect = getRect(wrapper);

    let score = 0;

    if (label.includes('mystery discount')) score += 5;
    if (popupRoot && wrapper && wrapper.parentElement === popupRoot) score += 2;
    if (isLauncherSized(buttonRect)) score += 2;
    if (isLauncherSized(wrapperRect)) score += 2;

    return {
      score,
      button,
      wrapper,
      offsetTarget: chooseOffsetTarget(wrapper, button)
    };
  }

  function findLauncher(shadowRoot) {
    if (!shadowRoot) return null;

    const popupRoot = findPopupRoot(shadowRoot);
    const buttons = Array.from(shadowRoot.querySelectorAll(BUTTON_SELECTOR));

    let bestMatch = null;

    buttons.forEach((button) => {
      const candidate = scoreButton(button, popupRoot);
      if (!candidate) return;

      if (!bestMatch || candidate.score > bestMatch.score) {
        bestMatch = candidate;
      }
    });

    return bestMatch;
  }

  function applyLauncherStyles(offsetTarget, button) {
    if (!offsetTarget || !button) {
      clearActiveStyles();
      return false;
    }

    if (activeOffsetTarget && activeOffsetTarget !== offsetTarget) {
      restoreStyles(activeOffsetTarget, OFFSET_STYLE_PROPS);
    }

    if (activeButton && activeButton !== button) {
      restoreStyles(activeButton, BUTTON_STYLE_PROPS);
    }

    setStyle(offsetTarget, 'translate', LAUNCHER_OFFSET);
    setStyle(button, 'border-radius', BUTTON_RADIUS, 'important');
    setStyle(button, 'overflow', 'hidden', 'important');

    activeOffsetTarget = offsetTarget;
    activeButton = button;

    return true;
  }

  function reconcileHost(host) {
    if (!host || !host.isConnected) {
      clearActiveStyles();
      return false;
    }

    if (!host.shadowRoot) {
      clearActiveStyles();
      return false;
    }

    stopBootstrapLoop();

    if (!shadowObserver) {
      observeShadowRoot(host);
    }

    if (isDialogOpen(host)) {
      clearActiveStyles();
      return true;
    }

    const launcher = findLauncher(host.shadowRoot);
    if (!launcher) {
      clearActiveStyles();
      return true;
    }

    return applyLauncherStyles(launcher.offsetTarget, launcher.button);
  }

  function scheduleReconcile(host) {
    activeHost = host || activeHost;

    if (rafScheduled) return;
    rafScheduled = true;

    window.requestAnimationFrame(() => {
      rafScheduled = false;

      const currentHost =
        activeHost && activeHost.isConnected ? activeHost : getHost();

      if (!currentHost) {
        activeHost = null;
        clearActiveStyles();
        disconnectHostObservers();
        stopBootstrapLoop();
        return;
      }

      activeHost = currentHost;
      reconcileHost(currentHost);
    });
  }

  function startBootstrapLoop(host) {
    if (!host) return;

    stopBootstrapLoop();
    bootstrapDeadline = Date.now() + BOOTSTRAP_TIMEOUT_MS;

    function tick() {
      if (!activeHost || activeHost !== host || !host.isConnected) {
        stopBootstrapLoop();
        return;
      }

      scheduleReconcile(host);

      if (host.shadowRoot || Date.now() >= bootstrapDeadline) {
        stopBootstrapLoop();
        return;
      }

      bootstrapTimer = window.setTimeout(tick, BOOTSTRAP_INTERVAL_MS);
    }

    tick();
  }

  function observeShadowRoot(host) {
    if (!host || !host.shadowRoot) return;
    if (shadowObserver) shadowObserver.disconnect();

    shadowObserver = new MutationObserver(() => {
      scheduleReconcile(host);
    });

    shadowObserver.observe(host.shadowRoot, {
      childList: true,
      subtree: true
    });
  }

  function observeHostAttributes(host) {
    if (!host) return;
    if (hostAttributeObserver) hostAttributeObserver.disconnect();

    hostAttributeObserver = new MutationObserver(() => {
      scheduleReconcile(host);
    });

    hostAttributeObserver.observe(host, {
      attributes: true,
      attributeFilter: ['role', 'aria-modal', 'class', 'style', 'hidden']
    });
  }

  function setActiveHost(host) {
    if (host === activeHost) {
      scheduleReconcile(host);
      return;
    }

    clearActiveStyles();
    stopBootstrapLoop();
    disconnectHostObservers();

    activeHost = host || null;

    if (!activeHost) return;

    observeHostAttributes(activeHost);

    if (activeHost.shadowRoot) {
      observeShadowRoot(activeHost);
    } else {
      startBootstrapLoop(activeHost);
    }

    scheduleReconcile(activeHost);
  }

  function observeDocument() {
    if (documentObserver) documentObserver.disconnect();

    documentObserver = new MutationObserver(() => {
      const host = getHost();

      if (!host) {
        setActiveHost(null);
        return;
      }

      if (host !== activeHost) {
        setActiveHost(host);
        return;
      }

      if (host.shadowRoot && !shadowObserver) {
        observeShadowRoot(host);
      }

      scheduleReconcile(host);
    });

    documentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function boot() {
    setActiveHost(getHost());
    observeDocument();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('load', boot, { once: true });
})();
